import {
    Component,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { functions } from '@masar/common/misc/functions';
import { normalizeContentType } from '@masar/common/utils';

@Component({
    selector: 'app-file-viewer',
    templateUrl: './file-viewer.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class FileViewerComponent implements OnInit, OnChanges {
    @HostBinding('class')
    public hostClass = 'block h-full w-full min-h-[60vh]';

    @Input() public src: string = '';
    @Input() public fileName: string = 'document';
    @Input() public contentType: string = '';

    public safeSrc: SafeResourceUrl | null = null;

    public get isImage(): boolean {
        if (this.contentType) {
            return normalizeContentType(this.contentType).startsWith('image/');
        }

        const extension = (this.fileName || '').split('.').pop()?.toLowerCase();
        return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(
            extension || ''
        );
    }

    // Decides whether the preview drops its sandbox, so it must rely only on
    // the verified content type. A display name is user controlled: a stored
    // SVG called "report.pdf" would otherwise execute script in this origin.
    public get isPdf(): boolean {
        return normalizeContentType(this.contentType) === 'application/pdf';
    }

    public constructor(private readonly sanitizer: DomSanitizer) {}

    public ngOnInit(): void {
        this.updateSafeSrc();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes.src) return;
        this.updateSafeSrc();
    }

    public download(): void {
        if (!this.src) return;

        fetch(this.src)
            .then(res => res.blob())
            .then(blob => {
                functions.downloadBlobIntoFile(blob, this.getSafeFileName());
            })
            .catch(() => {
                const link = document.createElement('a');
                link.href = this.src;
                link.setAttribute('download', this.getSafeFileName());
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
    }

    private updateSafeSrc(): void {
        this.safeSrc = this.src
            ? this.sanitizer.bypassSecurityTrustResourceUrl(this.src)
            : null;
    }

    private getSafeFileName(): string {
        return (this.fileName || 'document').replace(/[\\/:*?"<>|]/g, '_');
    }
}
